import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../../../api/client';
import { pendingBalanceOf } from '../../../utils/paymentAmount';
import type { OrderItem, PaginationMeta } from '../../../types';

export interface NettingOrder {
  id: string;
  orderNumber: string | null;
  orderType: string | null;
  orderStatus: string | null;
  paymentStatus: string | null;
  orderAmount: number;
  paidAmount: number;
  unpaidAmount: number;
  orderStartDate: string | null;
  customerId: string | null;
  customerName: string | null;
  serviceTypeName: string | null;
}

export interface CustomerNettingGroup {
  customerId: string;
  customerName: string;
  incomeOrders: NettingOrder[];
  expenseOrders: NettingOrder[];
  totalReceivable: number;
  totalPayable: number;
  nettableAmount: number;
}

/**
 * 没有对方的订单在界面上归到这一组。它只是 Map 的分组键，不是客户 id：
 * 后端 `customer_id` 走 uuid 契约，发出去只会换来 400，所以请求前必须还原成 null。
 */
const UNASSIGNED_CUSTOMER_KEY = 'unknown';

/** 分组键 → 后端要的对方客户 id：没有客户时是 null，不是界面占位串 */
function toRealCustomerId(groupId: string | undefined): string | null {
  return groupId && groupId !== UNASSIGNED_CUSTOMER_KEY ? groupId : null;
}

type RawOrderRow = {
  id: string;
  orderNumber: string | null;
  orderCategory: string | null;
  orderStatus: string | null;
  reviewStatus: string | null;
  paymentStatus: string | null;
  orderAmount: number | null;
  paidAmount: number | null;
  orderStartDate: string | null;
  customerId: string | null;
  customer: { id: string; fullName: string | null } | null;
  serviceType: { name: string | null } | null;
};

export function useOrderNetting() {
  const queryClient = useQueryClient();

  // 使用 useQuery 获取订单数据并处理
  const { data: groups = [], isLoading: loading, error, refetch: refresh } = useQuery({
    queryKey: ['orderNetting'],
    queryFn: async () => {
      const params = {
        paymentStatus: ['未付款', '部分付款'],
        orderBy: 'orderStartDate',
        orderDirection: 'desc',
        pageSize: 9999,
      };
      const res = await get<{ data: OrderItem[]; pagination: PaginationMeta }>('/orders', { params });
      if (!res?.data) throw new Error('获取订单数据失败');

      const rows = ((res.data ?? []) as unknown as RawOrderRow[])
        // 排除已取消和已拒绝的订单（不应参与抵消）
        .filter(r => r.orderStatus !== '已取消' && r.reviewStatus !== '已拒绝');

      // group by customer
      const customerMap = new Map<string, { name: string; income: NettingOrder[]; expense: NettingOrder[] }>();

      rows.forEach(r => {
        const cid = r.customer?.id || UNASSIGNED_CUSTOMER_KEY;
        const cname = r.customer?.fullName || '未知客户';
        if (!customerMap.has(cid)) {
          customerMap.set(cid, { name: cname, income: [], expense: [] });
        }
        const group = customerMap.get(cid)!;
        const orderAmount = Number(r.orderAmount) || 0;
        const paidAmount = Number(r.paidAmount) || 0;
        const unpaidAmount = pendingBalanceOf(orderAmount, paidAmount);
        const order: NettingOrder = {
          id: r.id,
          orderNumber: r.orderNumber,
          orderType: r.orderCategory,
          orderStatus: r.orderStatus,
          paymentStatus: r.paymentStatus,
          orderAmount: orderAmount,
          paidAmount: paidAmount,
          unpaidAmount: unpaidAmount,
          orderStartDate: r.orderStartDate,
          customerId: r.customer?.id || null,
          customerName: cname,
          serviceTypeName: (r.serviceType as { name: string | null } | null)?.name || null,
        };
        // 方向按「分类名含不含『支出』」判，与服务端 `isExpenseCategory` 同式：
        // 分类名是用户自建的（预设之外还能加「支出订单-外采」「外协服务」这类），
        // 精确等值 `=== '支出订单'` 会把自建分类的支出单当成收入单去配对轧差——方向反了，抵出的金额是错的。
        if ((r.orderCategory ?? '').includes('支出')) {
          group.expense.push(order);
        } else {
          group.income.push(order);
        }
      });

      // only keep customers with BOTH income and expense unpaid orders
      const result: CustomerNettingGroup[] = [];
      customerMap.forEach((val, cid) => {
        if (val.income.length > 0 && val.expense.length > 0) {
          const totalReceivable = val.income.reduce((s, o) => s + o.unpaidAmount, 0);
          const totalPayable = val.expense.reduce((s, o) => s + o.unpaidAmount, 0);
          result.push({
            customerId: cid,
            customerName: val.name,
            incomeOrders: val.income,
            expenseOrders: val.expense,
            totalReceivable,
            totalPayable,
            nettableAmount: Math.min(totalReceivable, totalPayable),
          });
        }
      });

      result.sort((a, b) => b.nettableAmount - a.nettableAmount);
      return result;
    },
  });

  // 使用 useMutation 执行订单抵消
  const executeNettingMutation = useMutation({
    mutationFn: async (params: {
      selectedOrderIds: string[];
      nettingAmount: number;
      mode: 'order' | 'amount';
      customerInfo?: { id: string; name: string };
    }) => {
      if (params.selectedOrderIds.length === 0) {
        throw new Error('请先选择要抵消的订单');
      }

      // 调用后端 API 执行订单抵消
      const response = await post<{ success: boolean; message: string; nettingLogId?: string; batchNo?: string; needsApproval?: boolean }>('/orders/netting', {
        orderIds: params.selectedOrderIds,
        nettingAmount: params.nettingAmount,
        mode: params.mode,
        customerId: toRealCustomerId(params.customerInfo?.id),
        customerName: params.customerInfo?.name || null
      });

      if (!response.success) {
        throw new Error(response.message || '执行抵消失败');
      }

      return response;
    },
    onSuccess: () => {
      // 刷新订单抵消数据
      queryClient.invalidateQueries({ queryKey: ['orderNetting'] });
      // 同时刷新订单数据
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      // 刷新核算管理数据
      queryClient.invalidateQueries({ queryKey: ['accountingData'] });
    },
  });

  // Execute netting: update selected orders paid_amount & payment_status
  const executeNetting = useCallback(async (
    selectedOrderIds: string[],
    nettingAmount: number,
    mode: 'order' | 'amount',
    customerInfo?: { id: string; name: string }
  ): Promise<{ success: boolean; message: string; nettingLogId?: string; batchNo?: string; needsApproval?: boolean }> => {
    try {
      const result = await executeNettingMutation.mutateAsync({
        selectedOrderIds,
        nettingAmount,
        mode,
        customerInfo
      });
      return {
        success: true,
        message: result.message || `抵消成功，共处理 ${selectedOrderIds.length} 笔订单`,
        nettingLogId: result.nettingLogId,
        batchNo: result.batchNo,
        needsApproval: result.needsApproval,
      };
    } catch (e: unknown) {
      return { success: false, message: e instanceof Error ? e.message : '执行失败，请重试' };
    }
  }, [executeNettingMutation]);

  return { groups, loading, error: error?.message || null, refresh, executeNetting };
}
